/** Compteurs produits par rubrique (aligné backend). */

export function normalizeRubriqueNom(nom) {
  if (nom === undefined || nom === null) return '';
  return String(nom).trim().replace(/\s+/g, ' ');
}

export function rubriqueCountKey(nom) {
  return normalizeRubriqueNom(nom).toLocaleLowerCase('fr');
}

export function buildProduitCountMaps(produits = []) {
  const byCategorie = new Map();
  const bySousCategorie = new Map();

  produits.forEach((p) => {
    const cat = rubriqueCountKey(p.categorie);
    if (!cat) return;
    byCategorie.set(cat, (byCategorie.get(cat) || 0) + 1);
    const sub = rubriqueCountKey(p.sousCategorie);
    if (sub) {
      const key = `${cat}::${sub}`;
      bySousCategorie.set(key, (bySousCategorie.get(key) || 0) + 1);
    }
  });

  return { byCategorie, bySousCategorie };
}

export function enrichCategoriesWithCounts(categories = [], produits = []) {
  if (!categories.length) return categories;
  if (!produits.length) return categories;

  const { byCategorie, bySousCategorie } = buildProduitCountMaps(produits);

  return categories.map((cat) => ({
    ...cat,
    produitCount: byCategorie.get(rubriqueCountKey(cat.nom)) || 0,
    sousCategories: (cat.sousCategories || []).map((sub) => ({
      ...sub,
      produitCount:
        bySousCategorie.get(`${rubriqueCountKey(cat.nom)}::${rubriqueCountKey(sub.nom)}`) || 0,
    })),
  }));
}

/** Liste des catégories pour filtres (ordre registre catalogue, puis orphelines). */
export function buildCategoryFilterChips(produits = [], catalogueCategories = []) {
  if (!produits?.length) return [];

  const fromProducts = [...new Set(produits.map((p) => p.categorie).filter(Boolean))];
  if (!catalogueCategories?.length) {
    return fromProducts.sort((a, b) => a.localeCompare(b, 'fr', { sensitivity: 'base' }));
  }

  const productSet = new Set(fromProducts);
  const ordered = catalogueCategories
    .filter((c) => c.actif !== false)
    .map((c) => c.nom)
    .filter((nom) => productSet.has(nom));

  const inRegistry = new Set(ordered);
  const extra = fromProducts
    .filter((n) => !inRegistry.has(n))
    .sort((a, b) => a.localeCompare(b, 'fr', { sensitivity: 'base' }));

  return [...ordered, ...extra];
}

/**
 * Produits d’une rubrique, triés par ordre puis nom.
 * Catégorie (sans sous-catégorie) : produits sans sous-catégorie.
 * Sous-catégorie : produits dont categorie + sousCategorie matchent.
 */
export function listProduitsInRubrique(produits = [], categorieNom, sousCategorieNom = null) {
  const catKey = rubriqueCountKey(categorieNom);
  const wantSub = normalizeRubriqueNom(sousCategorieNom);
  const subKey = wantSub ? rubriqueCountKey(wantSub) : '';

  return produits
    .filter((p) => {
      if (rubriqueCountKey(p.categorie) !== catKey) return false;
      const haveSub = normalizeRubriqueNom(p.sousCategorie);
      if (wantSub) return rubriqueCountKey(haveSub) === subKey;
      return !haveSub;
    })
    .sort((a, b) => {
      const ordreA = a.ordre ?? 0;
      const ordreB = b.ordre ?? 0;
      if (ordreA !== ordreB) return ordreA - ordreB;
      return (a.nom || '').localeCompare(b.nom || '', 'fr', { sensitivity: 'base' });
    });
}

/** Déplace l’élément `fromId` à la position de `toId` (même liste). */
export function moveItemInList(list, fromId, toId) {
  if (!fromId || !toId || fromId === toId) return list;
  const fromIdx = list.findIndex((item) => item.id === fromId);
  const toIdx = list.findIndex((item) => item.id === toId);
  if (fromIdx < 0 || toIdx < 0) return list;
  const next = [...list];
  const [item] = next.splice(fromIdx, 1);
  next.splice(toIdx, 0, item);
  return next;
}

/** Tri produits caisse : ordre rubriques catalogue → sous-cat → ordre produit. */
export function sortProduitsForPos(produits = [], catalogueRubriques = []) {
  const catOrdre = new Map();
  const sousOrdre = new Map();
  catalogueRubriques.forEach((cat, catIndex) => {
    catOrdre.set(rubriqueCountKey(cat.nom), cat.ordre ?? catIndex);
    (cat.sousCategories || []).forEach((sub, subIndex) => {
      sousOrdre.set(
        `${rubriqueCountKey(cat.nom)}::${rubriqueCountKey(sub.nom)}`,
        sub.ordre ?? subIndex
      );
    });
  });

  return [...produits].sort((a, b) => {
    const catKeyA = rubriqueCountKey(a.categorie);
    const catKeyB = rubriqueCountKey(b.categorie);
    const oCatA = catOrdre.has(catKeyA) ? catOrdre.get(catKeyA) : 999;
    const oCatB = catOrdre.has(catKeyB) ? catOrdre.get(catKeyB) : 999;
    if (oCatA !== oCatB) return oCatA - oCatB;

    const subA = rubriqueCountKey(a.sousCategorie);
    const subB = rubriqueCountKey(b.sousCategorie);
    if (Boolean(subA) !== Boolean(subB)) return subA ? 1 : -1;
    if (subA && subB) {
      const oSubA = sousOrdre.get(`${catKeyA}::${subA}`) ?? 999;
      const oSubB = sousOrdre.get(`${catKeyB}::${subB}`) ?? 999;
      if (oSubA !== oSubB) return oSubA - oSubB;
    }

    const ordreA = a.ordre ?? 0;
    const ordreB = b.ordre ?? 0;
    if (ordreA !== ordreB) return ordreA - ordreB;
    return (a.nom || '').localeCompare(b.nom || '', 'fr', { sensitivity: 'base' });
  });
}
